import { useState, FormEvent, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { tasksApi, knowledgeBasesApi, projectsApi, taskTagsApi, taskAssistantApi, kbTagsApi, ExistingTagSuggestion, NewTagSuggestion, TaskEnhanceResponse, ExistingProjectSuggestion, NewProjectSuggestion } from '../utils/api';
import { AxiosError } from 'axios';
import FileUploader from './FileUploader';
import type { KnowledgeBaseListItem, KBTag } from '../types/knowledgeBase';
import type { ProjectListItem } from '../types/project';
import type { TaskTagTreeNode } from '../types/task';

interface WizardStep {
  number: number;
  title: string;
}

const steps: WizardStep[] = [
  { number: 1, title: 'Task Details' },
  { number: 2, title: 'Upload Files' },
  { number: 3, title: 'Knowledge Bases' },
  { number: 4, title: 'Review' }
];

// Tag tree node component for selection
interface TagTreeNodeSelectProps {
  node: TaskTagTreeNode;
  level: number;
  selectedTagIds: number[];
  onToggle: (tagId: number) => void;
}

function TagTreeNodeSelect({ node, level, selectedTagIds, onToggle }: TagTreeNodeSelectProps) {
  const [isExpanded, setIsExpanded] = useState(true);
  const hasChildren = node.children && node.children.length > 0;
  const isSelected = selectedTagIds.includes(node.id);

  return (
    <div>
      <div
        className={`flex items-center py-1.5 px-2 hover:bg-gray-50 cursor-pointer ${
          isSelected ? 'bg-gray-50' : ''
        }`}
        style={{ paddingLeft: `${level * 16 + 8}px` }}
        onClick={() => onToggle(node.id)}
      >
        {hasChildren ? (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              setIsExpanded(!isExpanded);
            }}
            className="mr-1.5 w-4 h-4 flex items-center justify-center text-gray-400 hover:text-gray-600"
          >
            {isExpanded ? '▼' : '▶'}
          </button>
        ) : (
          <span className="mr-1.5 w-4" />
        )}

        <input
          type="checkbox"
          checked={isSelected}
          onChange={() => onToggle(node.id)}
          onClick={(e) => e.stopPropagation()}
          className="w-4 h-4 rounded border-gray-300 mr-2"
        />

        {node.color && (
          <span
            className="w-3 h-3 rounded-full mr-1.5 flex-shrink-0"
            style={{ backgroundColor: node.color }}
          />
        )}

        <span className={`text-sm ${isSelected ? 'font-medium text-gray-900' : 'text-gray-700'}`}>
          {node.name}
        </span>
      </div>

      {hasChildren && isExpanded && (
        <div>
          {node.children.map((child) => (
            <TagTreeNodeSelect
              key={child.id}
              node={child}
              level={level + 1}
              selectedTagIds={selectedTagIds}
              onToggle={onToggle}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export default function TaskCreationWizard() {
  const navigate = useNavigate();
  const location = useLocation();
  const [currentStep, setCurrentStep] = useState(1);

  // Form data
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [files, setFiles] = useState<File[]>([]);
  const [selectedKBIds, setSelectedKBIds] = useState<number[]>([]);
  const [selectedProjectId, setSelectedProjectId] = useState<number | null>(() => {
    // Check if projectId was passed via location state
    const state = location.state as { projectId?: number } | null;
    return state?.projectId || null;
  });

  // KB data
  const [availableKBs, setAvailableKBs] = useState<KnowledgeBaseListItem[]>([]);
  const [defaultKBIds, setDefaultKBIds] = useState<number[]>([]);
  const [isLoadingKBs, setIsLoadingKBs] = useState(false);

  // KB tag filtering
  const [kbTags, setKbTags] = useState<KBTag[]>([]);
  const [selectedKbTagFilter, setSelectedKbTagFilter] = useState<number | null>(null);

  // Project data
  const [availableProjects, setAvailableProjects] = useState<ProjectListItem[]>([]);
  const [isLoadingProjects, setIsLoadingProjects] = useState(false);

  // Tag data
  const [tagTree, setTagTree] = useState<TaskTagTreeNode[]>([]);
  const [selectedTagIds, setSelectedTagIds] = useState<number[]>([]);
  const [isLoadingTags, setIsLoadingTags] = useState(false);
  const [showTagTree, setShowTagTree] = useState(false);

  // New tag creation (inline)
  const [showNewTagForm, setShowNewTagForm] = useState(false);
  const [newTagName, setNewTagName] = useState('');
  const [newTagParentId, setNewTagParentId] = useState<number | null>(null);
  const [newTagColor, setNewTagColor] = useState<string | null>(null);
  const [isCreatingTag, setIsCreatingTag] = useState(false);

  // AI Auto-tagging
  const [aiSuggestions, setAiSuggestions] = useState<{
    existing: ExistingTagSuggestion[];
    new: NewTagSuggestion[];
  }>({ existing: [], new: [] });
  const [isLoadingAiSuggestions, setIsLoadingAiSuggestions] = useState(false);
  const [dismissedSuggestions, setDismissedSuggestions] = useState<Set<string>>(new Set());
  const abortControllerRef = useRef<AbortController | null>(null);
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // AI Task Enhancement
  const [aiEnhancement, setAiEnhancement] = useState<TaskEnhanceResponse | null>(null);
  const [isLoadingEnhancement, setIsLoadingEnhancement] = useState(false);
  const [showEnhancement, setShowEnhancement] = useState(true);
  const enhanceAbortRef = useRef<AbortController | null>(null);
  const enhanceDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // AI Project Suggestions
  const [projectSuggestions, setProjectSuggestions] = useState<{
    existing: ExistingProjectSuggestion[];
    new: NewProjectSuggestion[];
  }>({ existing: [], new: [] });
  const [isLoadingProjectSuggestions, setIsLoadingProjectSuggestions] = useState(false);
  const [dismissedProjectSuggestions, setDismissedProjectSuggestions] = useState<Set<string>>(new Set());
  const projectAbortRef = useRef<AbortController | null>(null);
  const projectDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // UI state
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Load projects and tags on mount
  useEffect(() => {
    loadProjects();
    loadTags();
  }, []);

  // Load KBs when reaching step 3
  useEffect(() => {
    if (currentStep === 3 && availableKBs.length === 0) {
      loadKnowledgeBases();
    }
  }, [currentStep]);

  // Debounced AI tag suggestions (triggered on title/description change)
  useEffect(() => {
    // Clear previous timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    // Cancel any in-flight request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // Only suggest if we have meaningful content
    if (title.trim().length < 3 && description.trim().length < 10) {
      setAiSuggestions({ existing: [], new: [] });
      setIsLoadingAiSuggestions(false);
      return;
    }

    // Debounce: wait 600ms after user stops typing
    debounceTimerRef.current = setTimeout(async () => {
      const controller = new AbortController();
      abortControllerRef.current = controller;

      try {
        setIsLoadingAiSuggestions(true);
        const suggestions = await taskTagsApi.suggestTags(
          title.trim(),
          description.trim(),
          controller.signal
        );

        // Filter out dismissed suggestions
        const filteredExisting = suggestions.existing_tags.filter(
          s => !dismissedSuggestions.has(`existing-${s.tag_id}`)
        );
        const filteredNew = suggestions.new_tags.filter(
          s => !dismissedSuggestions.has(`new-${s.name}`)
        );

        setAiSuggestions({
          existing: filteredExisting,
          new: filteredNew
        });
      } catch (err) {
        // Ignore abort errors - they're expected when text changes
        if ((err as Error).name !== 'CanceledError' && (err as Error).name !== 'AbortError') {
          console.error('AI suggestion error:', err);
        }
      } finally {
        setIsLoadingAiSuggestions(false);
      }
    }, 600);

    // Cleanup
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [title, description, dismissedSuggestions]);

  // Debounced AI task enhancement (triggered on title/description change)
  useEffect(() => {
    // Clear previous timer
    if (enhanceDebounceRef.current) {
      clearTimeout(enhanceDebounceRef.current);
    }

    // Cancel any in-flight request
    if (enhanceAbortRef.current) {
      enhanceAbortRef.current.abort();
    }

    // Trigger enhancement when title has meaningful content (description can be empty)
    if (title.trim().length < 5) {
      setAiEnhancement(null);
      setIsLoadingEnhancement(false);
      return;
    }

    // Debounce: wait 1000ms after user stops typing
    enhanceDebounceRef.current = setTimeout(async () => {
      const controller = new AbortController();
      enhanceAbortRef.current = controller;

      try {
        setIsLoadingEnhancement(true);
        const result = await taskAssistantApi.enhance(
          { title: title.trim(), description: description.trim() },
          controller.signal
        );
        setAiEnhancement(result);
        setShowEnhancement(true);
      } catch (err) {
        // Ignore abort errors
        if ((err as Error).name !== 'CanceledError' && (err as Error).name !== 'AbortError') {
          console.error('AI enhancement error:', err);
        }
      } finally {
        setIsLoadingEnhancement(false);
      }
    }, 1000);

    // Cleanup
    return () => {
      if (enhanceDebounceRef.current) {
        clearTimeout(enhanceDebounceRef.current);
      }
    };
  }, [title, description]);

  // Apply AI enhanced version to form
  const applyEnhancement = () => {
    if (aiEnhancement) {
      setTitle(aiEnhancement.enhanced_title);
      setDescription(aiEnhancement.enhanced_description);
      setAiEnhancement(null);
      setShowEnhancement(false);
    }
  };

  // Debounced AI project suggestions (triggered on title/description change)
  useEffect(() => {
    // Clear previous timer
    if (projectDebounceRef.current) {
      clearTimeout(projectDebounceRef.current);
    }

    // Cancel any in-flight request
    if (projectAbortRef.current) {
      projectAbortRef.current.abort();
    }

    // Only suggest if we have meaningful content AND user hasn't already selected a project
    if (selectedProjectId !== null || (title.trim().length < 5 && description.trim().length < 10)) {
      setProjectSuggestions({ existing: [], new: [] });
      setIsLoadingProjectSuggestions(false);
      return;
    }

    // Debounce: wait 800ms after user stops typing
    projectDebounceRef.current = setTimeout(async () => {
      const controller = new AbortController();
      projectAbortRef.current = controller;

      try {
        setIsLoadingProjectSuggestions(true);
        const suggestions = await projectsApi.suggest(
          title.trim(),
          description.trim(),
          controller.signal
        );

        // Filter out dismissed suggestions
        const filteredExisting = suggestions.existing_projects.filter(
          s => !dismissedProjectSuggestions.has(`existing-${s.project_id}`)
        );
        const filteredNew = suggestions.new_projects.filter(
          s => !dismissedProjectSuggestions.has(`new-${s.name}`)
        );

        setProjectSuggestions({
          existing: filteredExisting,
          new: filteredNew
        });
      } catch (err) {
        // Ignore abort errors
        if ((err as Error).name !== 'CanceledError' && (err as Error).name !== 'AbortError') {
          console.error('Project suggestion error:', err);
        }
      } finally {
        setIsLoadingProjectSuggestions(false);
      }
    }, 800);

    // Cleanup
    return () => {
      if (projectDebounceRef.current) {
        clearTimeout(projectDebounceRef.current);
      }
    };
  }, [title, description, selectedProjectId, dismissedProjectSuggestions]);

  // Accept an existing project suggestion
  const acceptExistingProjectSuggestion = (suggestion: ExistingProjectSuggestion) => {
    setSelectedProjectId(suggestion.project_id);
    // Clear all suggestions since user has selected a project
    setProjectSuggestions({ existing: [], new: [] });
  };

  // Accept a new project suggestion (create it and select it)
  const acceptNewProjectSuggestion = async (suggestion: NewProjectSuggestion) => {
    try {
      const newProject = await projectsApi.create({
        name: suggestion.name,
        description: suggestion.description
      });
      await loadProjects();
      setSelectedProjectId(newProject.id);
      // Clear all suggestions since user has selected a project
      setProjectSuggestions({ existing: [], new: [] });
    } catch (err) {
      console.error('Failed to create suggested project:', err);
    }
  };

  // Dismiss a project suggestion
  const dismissProjectSuggestion = (type: 'existing' | 'new', id: string) => {
    setDismissedProjectSuggestions(prev => new Set([...prev, `${type}-${id}`]));
    if (type === 'existing') {
      setProjectSuggestions(prev => ({
        ...prev,
        existing: prev.existing.filter(s => String(s.project_id) !== id)
      }));
    } else {
      setProjectSuggestions(prev => ({
        ...prev,
        new: prev.new.filter(s => s.name !== id)
      }));
    }
  };

  // Accept an existing tag suggestion
  const acceptExistingSuggestion = (suggestion: ExistingTagSuggestion) => {
    if (!selectedTagIds.includes(suggestion.tag_id)) {
      setSelectedTagIds([...selectedTagIds, suggestion.tag_id]);
    }
    // Remove from suggestions
    setAiSuggestions(prev => ({
      ...prev,
      existing: prev.existing.filter(s => s.tag_id !== suggestion.tag_id)
    }));
  };

  // Accept a new tag suggestion (create it and select it)
  const acceptNewTagSuggestion = async (suggestion: NewTagSuggestion) => {
    try {
      const newTag = await taskTagsApi.create({
        name: suggestion.name,
        parent_id: suggestion.parent_id,
        color: null
      });
      await loadTags();
      setSelectedTagIds([...selectedTagIds, newTag.id]);
      // Remove from suggestions
      setAiSuggestions(prev => ({
        ...prev,
        new: prev.new.filter(s => s.name !== suggestion.name)
      }));
    } catch (err) {
      console.error('Failed to create suggested tag:', err);
    }
  };

  // Dismiss a suggestion
  const dismissSuggestion = (type: 'existing' | 'new', id: string) => {
    setDismissedSuggestions(prev => new Set([...prev, `${type}-${id}`]));
    if (type === 'existing') {
      setAiSuggestions(prev => ({
        ...prev,
        existing: prev.existing.filter(s => String(s.tag_id) !== id)
      }));
    } else {
      setAiSuggestions(prev => ({
        ...prev,
        new: prev.new.filter(s => s.name !== id)
      }));
    }
  };

  const loadProjects = async () => {
    try {
      setIsLoadingProjects(true);
      const projects = await projectsApi.getAll(false);
      setAvailableProjects(projects);
    } catch (err) {
      console.error('Failed to load projects:', err);
    } finally {
      setIsLoadingProjects(false);
    }
  };

  const loadTags = async () => {
    try {
      setIsLoadingTags(true);
      const tree = await taskTagsApi.getTree();
      setTagTree(tree);
    } catch (err) {
      console.error('Failed to load tags:', err);
    } finally {
      setIsLoadingTags(false);
    }
  };

  const toggleTagSelection = (tagId: number) => {
    if (selectedTagIds.includes(tagId)) {
      setSelectedTagIds(selectedTagIds.filter(id => id !== tagId));
    } else {
      setSelectedTagIds([...selectedTagIds, tagId]);
    }
  };

  // Helper to find tag by ID in tree
  const findTagInTree = (nodes: TaskTagTreeNode[], id: number): TaskTagTreeNode | null => {
    for (const node of nodes) {
      if (node.id === id) return node;
      const found = findTagInTree(node.children, id);
      if (found) return found;
    }
    return null;
  };

  // Get full path for a tag
  const getTagPath = (tagId: number): string => {
    const buildPath = (nodes: TaskTagTreeNode[], targetId: number, path: string[]): string[] | null => {
      for (const node of nodes) {
        if (node.id === targetId) {
          return [...path, node.name];
        }
        const found = buildPath(node.children, targetId, [...path, node.name]);
        if (found) return found;
      }
      return null;
    };
    const path = buildPath(tagTree, tagId, []);
    return path ? path.join(' → ') : '';
  };

  // Color palette for new tags
  const tagColors = [
    '#EF4444', '#F97316', '#F59E0B', '#84CC16', '#22C55E',
    '#14B8A6', '#0EA5E9', '#3B82F6', '#6366F1', '#8B5CF6',
    '#A855F7', '#EC4899', '#6B7280'
  ];

  // Flatten tag tree for parent selection
  const getFlatTagsForParent = (): Array<{ id: number; name: string; depth: number }> => {
    const result: Array<{ id: number; name: string; depth: number }> = [];
    const traverse = (nodes: TaskTagTreeNode[], depth: number) => {
      for (const node of nodes) {
        result.push({ id: node.id, name: node.name, depth });
        traverse(node.children, depth + 1);
      }
    };
    traverse(tagTree, 0);
    return result;
  };

  // Handle creating a new tag inline
  const handleCreateTag = async () => {
    if (!newTagName.trim()) return;

    try {
      setIsCreatingTag(true);
      const newTag = await taskTagsApi.create({
        name: newTagName.trim(),
        parent_id: newTagParentId,
        color: newTagColor
      });

      // Reload tags and auto-select the new tag
      await loadTags();
      setSelectedTagIds([...selectedTagIds, newTag.id]);

      // Reset form
      setNewTagName('');
      setNewTagParentId(null);
      setNewTagColor(null);
      setShowNewTagForm(false);
    } catch (err) {
      console.error('Failed to create tag:', err);
    } finally {
      setIsCreatingTag(false);
    }
  };

  const loadKnowledgeBases = async () => {
    try {
      setIsLoadingKBs(true);

      // Load KBs, defaults, and KB tags in parallel
      const [kbs, defaultsResponse, tags] = await Promise.all([
        knowledgeBasesApi.getAll(false), // Only active KBs
        fetch('/api/knowledge-bases/defaults', {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }),
        kbTagsApi.getAll()
      ]);

      setAvailableKBs(kbs);
      setKbTags(tags.filter(t => t.kb_count > 0)); // Only show tags that have KBs

      // Process defaults response
      if (defaultsResponse.ok) {
        const data = await defaultsResponse.json();
        const defaults = data.knowledge_base_ids || [];
        setDefaultKBIds(defaults);
        setSelectedKBIds(defaults); // Pre-select defaults
      }
    } catch (err) {
      console.error('Failed to load knowledge bases:', err);
    } finally {
      setIsLoadingKBs(false);
    }
  };

  const toggleKBSelection = (kbId: number) => {
    // Allow toggling any KB, including defaults
    if (selectedKBIds.includes(kbId)) {
      setSelectedKBIds(selectedKBIds.filter(id => id !== kbId));
    } else {
      setSelectedKBIds([...selectedKBIds, kbId]);
    }
  };

  const handleNext = (e?: React.MouseEvent<HTMLButtonElement>) => {
    e?.preventDefault();
    e?.stopPropagation();
    if (currentStep < 4) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleBack = (e?: React.MouseEvent<HTMLButtonElement>) => {
    e?.preventDefault();
    e?.stopPropagation();
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSubmit = async (e: FormEvent, isDraft: boolean = false) => {
    e.preventDefault();
    setError('');
    setIsSubmitting(true);

    try {
      // Create task with KB associations and project
      const task = await tasksApi.create({
        title,
        description,
        knowledge_base_ids: selectedKBIds,
        is_draft: isDraft,
        project_id: selectedProjectId
      });

      // Upload files if any
      if (files.length > 0) {
        await tasksApi.uploadFiles(task.id, files);
      }

      // Add tags if any selected
      if (selectedTagIds.length > 0) {
        await Promise.all(
          selectedTagIds.map(tagId => taskTagsApi.addTagToTask(task.id, tagId))
        );
      }

      // Navigate to task detail
      navigate(`/tasks/${task.id}`);
    } catch (err) {
      const axiosError = err as AxiosError<{ detail: string }>;
      setError(axiosError.response?.data?.detail || 'Failed to create task');
    } finally {
      setIsSubmitting(false);
    }
  };

  const canProceedFromStep1 = title.trim().length > 0 && description.trim().length > 0;

  return (
    <div className="flex gap-8">
      {/* Vertical Progress Steps - Left Sidebar */}
      <div className="w-48 flex-shrink-0">
        <div className="sticky top-8 space-y-4">
          {steps.map((step, index) => (
            <div key={step.number} className="relative">
              <div className="flex items-center space-x-3">
                <div className={`flex items-center justify-center w-10 h-10 rounded-full border-2 transition-colors flex-shrink-0 ${
                  currentStep >= step.number
                    ? 'border-black bg-black text-white'
                    : 'border-gray-300 text-gray-500'
                }`}>
                  {currentStep > step.number ? (
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  ) : (
                    <span className="font-medium">{step.number}</span>
                  )}
                </div>
                <p className={`text-sm font-medium ${currentStep >= step.number ? 'text-black' : 'text-gray-500'}`}>
                  {step.title}
                </p>
              </div>
              {index < steps.length - 1 && (
                <div className={`absolute left-5 top-10 w-0.5 h-8 transition-colors ${
                  currentStep > step.number ? 'bg-black' : 'bg-gray-300'
                }`} />
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Step Content */}
      <div className="flex-1 bg-white border border-gray-200 rounded-lg p-8 shadow-subtle">
        <form onSubmit={handleSubmit}>
          {/* Step 1: Task Details */}
          {currentStep === 1 && (
            <div className="space-y-6 animate-fade-in">
              <div>
                <h2 className="text-2xl font-bold mb-2">What would you like the agent to do?</h2>
                <p className="text-gray-600">Provide a clear title and detailed description of your task</p>
              </div>

              <div>
                <label htmlFor="wizard-title" className="block text-sm font-medium text-gray-700 mb-2">
                  Task Title *
                </label>
                <input
                  id="wizard-title"
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-200 rounded-md focus:border-black focus:ring-1 focus:ring-black"
                  placeholder="e.g., Analyze Q3 Sales Data"
                  required
                  maxLength={200}
                  autoFocus
                />
                <p className="text-xs text-gray-500 mt-1">{title.length}/200 characters</p>
              </div>

              <div>
                <label htmlFor="wizard-project" className="block text-sm font-medium text-gray-700 mb-2">
                  Project (Optional)
                </label>
                <select
                  id="wizard-project"
                  value={selectedProjectId || ''}
                  onChange={(e) => setSelectedProjectId(e.target.value ? parseInt(e.target.value) : null)}
                  className="w-full px-4 py-2 border border-gray-200 rounded-md focus:border-black focus:ring-1 focus:ring-black"
                  disabled={isLoadingProjects}
                >
                  <option value="">No Project</option>
                  {availableProjects.map(project => (
                    <option key={project.id} value={project.id}>
                      {project.name}
                    </option>
                  ))}
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  Assign this task to a project for better organization
                </p>

                {/* AI Project Suggestions */}
                {(isLoadingProjectSuggestions || projectSuggestions.existing.length > 0 || projectSuggestions.new.length > 0) && (
                  <div className="mt-3 p-3 bg-emerald-50 border border-emerald-200 rounded-lg animate-fade-in">
                    <div className="flex items-center gap-2 mb-2">
                      {isLoadingProjectSuggestions ? (
                        <>
                          <span className="animate-spin rounded-full h-3.5 w-3.5 border-2 border-emerald-600 border-t-transparent"></span>
                          <span className="text-xs font-medium text-emerald-700">AI analyzing...</span>
                        </>
                      ) : (
                        <>
                          <span className="text-emerald-600">📁</span>
                          <span className="text-xs font-medium text-emerald-700">AI Project Suggestions</span>
                        </>
                      )}
                    </div>

                    {!isLoadingProjectSuggestions && (
                      <div className="space-y-2">
                        {/* Existing project suggestions */}
                        {projectSuggestions.existing.map((suggestion) => (
                          <div
                            key={`existing-${suggestion.project_id}`}
                            className="p-2 bg-white rounded border border-emerald-100"
                          >
                            <div className="flex items-start justify-between gap-2">
                              <div className="min-w-0 flex-1">
                                <div className="flex items-center gap-2">
                                  <span className="text-sm font-medium text-gray-900">
                                    {suggestion.project_name}
                                  </span>
                                  <span className="text-xs text-gray-500">
                                    ({suggestion.task_count} task{suggestion.task_count !== 1 ? 's' : ''})
                                  </span>
                                </div>
                                <span className="text-xs text-gray-500 block mt-0.5">
                                  {suggestion.reason}
                                </span>
                              </div>
                              <div className="flex items-center gap-1 flex-shrink-0">
                                <span className="text-xs text-emerald-600 font-medium">
                                  {Math.round(suggestion.confidence * 100)}%
                                </span>
                                <button
                                  type="button"
                                  onClick={() => acceptExistingProjectSuggestion(suggestion)}
                                  className="px-2 py-1 text-xs bg-emerald-600 text-white rounded hover:bg-emerald-700"
                                >
                                  Select
                                </button>
                                <button
                                  type="button"
                                  onClick={() => dismissProjectSuggestion('existing', String(suggestion.project_id))}
                                  className="p-1 text-gray-400 hover:text-gray-600"
                                >
                                  ×
                                </button>
                              </div>
                            </div>
                          </div>
                        ))}

                        {/* New project suggestions */}
                        {projectSuggestions.new.map((suggestion) => (
                          <div
                            key={`new-${suggestion.name}`}
                            className="p-2 bg-white rounded border border-emerald-100"
                          >
                            <div className="flex items-start justify-between gap-2">
                              <div className="flex items-start gap-2 flex-1 min-w-0">
                                <span className="w-4 h-4 rounded bg-emerald-200 flex-shrink-0 flex items-center justify-center text-emerald-600 text-xs mt-0.5">+</span>
                                <div className="min-w-0 flex-1">
                                  <div className="flex items-center gap-2">
                                    <span className="text-sm font-medium text-gray-900">
                                      {suggestion.name}
                                    </span>
                                    <span className="text-xs text-emerald-600">(new)</span>
                                  </div>
                                  {suggestion.description && (
                                    <span className="text-xs text-gray-600 block mt-0.5">
                                      {suggestion.description}
                                    </span>
                                  )}
                                  <span className="text-xs text-gray-500 block mt-0.5">
                                    {suggestion.reason}
                                  </span>
                                </div>
                              </div>
                              <div className="flex items-center gap-1 flex-shrink-0">
                                <span className="text-xs text-emerald-600 font-medium">
                                  {Math.round(suggestion.confidence * 100)}%
                                </span>
                                <button
                                  type="button"
                                  onClick={() => acceptNewProjectSuggestion(suggestion)}
                                  className="px-2 py-1 text-xs bg-emerald-600 text-white rounded hover:bg-emerald-700"
                                >
                                  Create
                                </button>
                                <button
                                  type="button"
                                  onClick={() => dismissProjectSuggestion('new', suggestion.name)}
                                  className="p-1 text-gray-400 hover:text-gray-600"
                                >
                                  ×
                                </button>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div>
                <label htmlFor="wizard-description" className="block text-sm font-medium text-gray-700 mb-2">
                  Detailed Description *
                </label>
                <textarea
                  id="wizard-description"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-200 rounded-md focus:border-black focus:ring-1 focus:ring-black"
                  placeholder="Describe in detail what you need the agent to analyze, process, or generate..."
                  required
                  rows={8}
                />
                <p className="text-xs text-gray-500 mt-1">
                  Be specific about what you want the agent to focus on
                </p>
              </div>

              {/* AI Enhancement Panel */}
              {(isLoadingEnhancement || (aiEnhancement && showEnhancement)) && (
                <div className="p-4 bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg animate-fade-in">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      {isLoadingEnhancement ? (
                        <>
                          <span className="animate-spin rounded-full h-4 w-4 border-2 border-blue-600 border-t-transparent"></span>
                          <span className="text-sm font-medium text-blue-700">AI is enhancing your task...</span>
                        </>
                      ) : (
                        <>
                          <span className="text-blue-600 text-lg">🤖</span>
                          <span className="text-sm font-medium text-blue-700">AI-Enhanced Version</span>
                          {aiEnhancement?.detected_type && (
                            <span className="px-2 py-0.5 bg-blue-100 text-blue-600 text-xs rounded-full capitalize">
                              {aiEnhancement.detected_type.replace('_', ' ')}
                            </span>
                          )}
                        </>
                      )}
                    </div>
                    {!isLoadingEnhancement && (
                      <button
                        type="button"
                        onClick={() => setShowEnhancement(false)}
                        className="text-blue-400 hover:text-blue-600 text-lg"
                        title="Dismiss"
                      >
                        ×
                      </button>
                    )}
                  </div>

                  {!isLoadingEnhancement && aiEnhancement && (
                    <>
                      {/* Enhanced Title */}
                      <div className="mb-3">
                        <label className="text-xs font-medium text-blue-600 mb-1 block">Enhanced Title:</label>
                        <div className="bg-white rounded border border-blue-100 px-3 py-2 text-sm text-gray-900">
                          {aiEnhancement.enhanced_title}
                        </div>
                      </div>

                      {/* Enhanced Description */}
                      <div className="mb-3">
                        <label className="text-xs font-medium text-blue-600 mb-1 block">Enhanced Description:</label>
                        <div className="bg-white rounded border border-blue-100 px-3 py-2 text-sm text-gray-900 max-h-48 overflow-y-auto whitespace-pre-wrap">
                          {aiEnhancement.enhanced_description}
                        </div>
                      </div>

                      {/* Assumptions */}
                      {aiEnhancement.assumptions && aiEnhancement.assumptions.length > 0 && (
                        <div className="mb-3">
                          <label className="text-xs font-medium text-amber-600 mb-1 block">⚠️ Assumptions made:</label>
                          <ul className="text-xs text-amber-700 bg-amber-50 rounded px-3 py-2 space-y-1">
                            {aiEnhancement.assumptions.map((assumption, idx) => (
                              <li key={idx}>• {assumption}</li>
                            ))}
                          </ul>
                        </div>
                      )}

                      {/* Actions */}
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={applyEnhancement}
                          className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 transition-colors flex items-center gap-2"
                        >
                          <span>✓</span> Apply Enhanced Version
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            setAiEnhancement(null);
                            // Trigger re-fetch
                            if (enhanceAbortRef.current) {
                              enhanceAbortRef.current.abort();
                            }
                            const controller = new AbortController();
                            enhanceAbortRef.current = controller;
                            setIsLoadingEnhancement(true);
                            taskAssistantApi.enhance(
                              { title: title.trim(), description: description.trim() },
                              controller.signal
                            ).then(result => {
                              setAiEnhancement(result);
                              setShowEnhancement(true);
                            }).catch(() => {}).finally(() => {
                              setIsLoadingEnhancement(false);
                            });
                          }}
                          className="px-4 py-2 bg-white text-blue-600 text-sm font-medium rounded-md border border-blue-200 hover:bg-blue-50 transition-colors flex items-center gap-2"
                        >
                          <span>🔄</span> Regenerate
                        </button>
                      </div>
                    </>
                  )}
                </div>
              )}

              {/* Tags Section */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Tags (Optional)
                  </label>
                  <div className="flex items-center gap-3">
                    <button
                      type="button"
                      onClick={() => {
                        setShowNewTagForm(!showNewTagForm);
                        if (!showTagTree) setShowTagTree(true);
                      }}
                      className="text-xs text-green-600 hover:text-green-800 font-medium flex items-center gap-1"
                    >
                      <span className="text-sm">+</span> New Tag
                    </button>
                    <a
                      href="/task-tags"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs text-gray-500 hover:text-gray-700"
                    >
                      Manage All
                    </a>
                  </div>
                </div>

                {/* Inline New Tag Form */}
                {showNewTagForm && (
                  <div className="mb-3 p-4 bg-green-50 border border-green-200 rounded-lg animate-fade-in">
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-sm font-medium text-green-800">Create New Tag</span>
                      <button
                        type="button"
                        onClick={() => {
                          setShowNewTagForm(false);
                          setNewTagName('');
                          setNewTagParentId(null);
                          setNewTagColor(null);
                        }}
                        className="text-green-600 hover:text-green-800"
                      >
                        ×
                      </button>
                    </div>

                    <div className="space-y-3">
                      {/* Tag Name */}
                      <input
                        type="text"
                        value={newTagName}
                        onChange={(e) => setNewTagName(e.target.value)}
                        placeholder="Tag name..."
                        className="w-full px-3 py-2 text-sm border border-green-200 rounded-md focus:border-green-500 focus:ring-1 focus:ring-green-500 bg-white"
                        autoFocus
                      />

                      {/* Parent Tag (optional) */}
                      <select
                        value={newTagParentId ?? ''}
                        onChange={(e) => setNewTagParentId(e.target.value ? parseInt(e.target.value) : null)}
                        className="w-full px-3 py-2 text-sm border border-green-200 rounded-md focus:border-green-500 focus:ring-1 focus:ring-green-500 bg-white"
                      >
                        <option value="">No parent (root tag)</option>
                        {getFlatTagsForParent().map((tag) => (
                          <option key={tag.id} value={tag.id}>
                            {'  '.repeat(tag.depth)}{tag.depth > 0 ? '└ ' : ''}{tag.name}
                          </option>
                        ))}
                      </select>

                      {/* Color Picker */}
                      <div>
                        <span className="text-xs text-green-700 mb-1.5 block">Color (optional)</span>
                        <div className="flex flex-wrap gap-1.5">
                          <button
                            type="button"
                            onClick={() => setNewTagColor(null)}
                            className={`w-6 h-6 rounded-full border-2 flex items-center justify-center text-xs ${
                              newTagColor === null ? 'border-green-600' : 'border-gray-200'
                            }`}
                            title="No color"
                          >
                            ∅
                          </button>
                          {tagColors.map((color) => (
                            <button
                              key={color}
                              type="button"
                              onClick={() => setNewTagColor(color)}
                              className={`w-6 h-6 rounded-full border-2 transition-transform ${
                                newTagColor === color ? 'border-green-600 scale-110' : 'border-transparent'
                              }`}
                              style={{ backgroundColor: color }}
                              title={color}
                            />
                          ))}
                        </div>
                      </div>

                      {/* Create Button */}
                      <button
                        type="button"
                        onClick={handleCreateTag}
                        disabled={!newTagName.trim() || isCreatingTag}
                        className="w-full px-3 py-2 bg-green-600 text-white text-sm font-medium rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      >
                        {isCreatingTag ? (
                          <span className="flex items-center justify-center gap-2">
                            <span className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></span>
                            Creating...
                          </span>
                        ) : (
                          'Create & Select Tag'
                        )}
                      </button>
                    </div>
                  </div>
                )}

                {/* AI Tag Suggestions */}
                {(isLoadingAiSuggestions || aiSuggestions.existing.length > 0 || aiSuggestions.new.length > 0) && (
                  <div className="mb-3 p-3 bg-purple-50 border border-purple-200 rounded-lg">
                    <div className="flex items-center gap-2 mb-2">
                      {isLoadingAiSuggestions ? (
                        <>
                          <span className="animate-spin rounded-full h-3.5 w-3.5 border-2 border-purple-600 border-t-transparent"></span>
                          <span className="text-xs font-medium text-purple-700">AI analyzing...</span>
                        </>
                      ) : (
                        <>
                          <span className="text-purple-600">✨</span>
                          <span className="text-xs font-medium text-purple-700">AI Suggestions</span>
                        </>
                      )}
                    </div>

                    {!isLoadingAiSuggestions && (
                      <div className="space-y-2">
                        {/* Existing tag suggestions */}
                        {aiSuggestions.existing.map((suggestion) => (
                          <div
                            key={`existing-${suggestion.tag_id}`}
                            className="p-2 bg-white rounded border border-purple-100"
                          >
                            <div className="flex items-start justify-between gap-2">
                              <div className="flex items-start gap-2 flex-1 min-w-0">
                                {suggestion.color && (
                                  <span
                                    className="w-3 h-3 rounded-full flex-shrink-0 mt-0.5"
                                    style={{ backgroundColor: suggestion.color }}
                                  />
                                )}
                                <div className="min-w-0 flex-1">
                                  <span className="text-sm font-medium text-gray-900 block">
                                    {suggestion.tag_path}
                                  </span>
                                  <span className="text-xs text-gray-500 block mt-0.5">
                                    {suggestion.reason}
                                  </span>
                                </div>
                              </div>
                              <div className="flex items-center gap-1 flex-shrink-0">
                                <span className="text-xs text-purple-600 font-medium">
                                  {Math.round(suggestion.confidence * 100)}%
                                </span>
                                <button
                                  type="button"
                                  onClick={() => acceptExistingSuggestion(suggestion)}
                                  className="px-2 py-1 text-xs bg-purple-600 text-white rounded hover:bg-purple-700"
                                >
                                  Add
                                </button>
                                <button
                                  type="button"
                                  onClick={() => dismissSuggestion('existing', String(suggestion.tag_id))}
                                  className="p-1 text-gray-400 hover:text-gray-600"
                                >
                                  ×
                                </button>
                              </div>
                            </div>
                          </div>
                        ))}

                        {/* New tag suggestions */}
                        {aiSuggestions.new.map((suggestion) => (
                          <div
                            key={`new-${suggestion.name}`}
                            className="p-2 bg-white rounded border border-purple-100"
                          >
                            <div className="flex items-start justify-between gap-2">
                              <div className="flex items-start gap-2 flex-1 min-w-0">
                                <span className="w-3 h-3 rounded-full bg-purple-200 flex-shrink-0 flex items-center justify-center text-purple-600 text-[8px] mt-0.5">+</span>
                                <div className="min-w-0 flex-1">
                                  <span className="text-sm font-medium text-gray-900 block">
                                    {suggestion.parent_name ? `${suggestion.parent_name} > ` : ''}{suggestion.name}
                                    <span className="text-xs text-purple-600 ml-1">(new)</span>
                                  </span>
                                  <span className="text-xs text-gray-500 block mt-0.5">
                                    {suggestion.reason}
                                  </span>
                                </div>
                              </div>
                              <div className="flex items-center gap-1 flex-shrink-0">
                                <span className="text-xs text-purple-600 font-medium">
                                  {Math.round(suggestion.confidence * 100)}%
                                </span>
                                <button
                                  type="button"
                                  onClick={() => acceptNewTagSuggestion(suggestion)}
                                className="px-2 py-1 text-xs bg-purple-600 text-white rounded hover:bg-purple-700"
                              >
                                Create
                              </button>
                              <button
                                type="button"
                                onClick={() => dismissSuggestion('new', suggestion.name)}
                                className="p-1 text-gray-400 hover:text-gray-600"
                              >
                                ×
                              </button>
                            </div>
                          </div>
                        </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* Selected Tags Display */}
                {selectedTagIds.length > 0 && (
                  <div className="flex flex-wrap gap-2 mb-3">
                    {selectedTagIds.map(tagId => {
                      const tag = findTagInTree(tagTree, tagId);
                      if (!tag) return null;
                      return (
                        <span
                          key={tagId}
                          className="inline-flex items-center px-2.5 py-1 rounded-full text-sm bg-gray-100 border border-gray-200"
                        >
                          {tag.color && (
                            <span
                              className="w-2.5 h-2.5 rounded-full mr-1.5"
                              style={{ backgroundColor: tag.color }}
                            />
                          )}
                          <span className="text-gray-700">{tag.name}</span>
                          <button
                            type="button"
                            onClick={() => toggleTagSelection(tagId)}
                            className="ml-1.5 text-gray-400 hover:text-gray-600"
                          >
                            ×
                          </button>
                        </span>
                      );
                    })}
                  </div>
                )}

                {/* Tag Tree Toggle Button */}
                <button
                  type="button"
                  onClick={() => setShowTagTree(!showTagTree)}
                  className="w-full px-4 py-2.5 border border-gray-200 rounded-md text-left text-sm text-gray-600 hover:bg-gray-50 transition-colors flex items-center justify-between"
                >
                  <span>
                    {selectedTagIds.length === 0
                      ? 'Click to select tags...'
                      : `${selectedTagIds.length} tag${selectedTagIds.length !== 1 ? 's' : ''} selected`}
                  </span>
                  <span className="text-gray-400">
                    {showTagTree ? '▲' : '▼'}
                  </span>
                </button>

                {/* Tag Tree */}
                {showTagTree && (
                  <div className="mt-2 border border-gray-200 rounded-md max-h-64 overflow-y-auto">
                    {isLoadingTags ? (
                      <div className="flex items-center justify-center py-6">
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-black"></div>
                        <span className="ml-2 text-sm text-gray-500">Loading tags...</span>
                      </div>
                    ) : tagTree.length === 0 && !showNewTagForm ? (
                      <div className="py-6 text-center">
                        <p className="text-sm text-gray-500 mb-2">No tags yet</p>
                        <button
                          type="button"
                          onClick={() => setShowNewTagForm(true)}
                          className="text-sm text-green-600 hover:text-green-800 font-medium"
                        >
                          + Create your first tag
                        </button>
                      </div>
                    ) : tagTree.length > 0 ? (
                      <div className="py-1">
                        {tagTree.map(node => (
                          <TagTreeNodeSelect
                            key={node.id}
                            node={node}
                            level={0}
                            selectedTagIds={selectedTagIds}
                            onToggle={toggleTagSelection}
                          />
                        ))}
                      </div>
                    ) : null}
                  </div>
                )}
                <p className="text-xs text-gray-500 mt-1">
                  Organize your task with tags for easy filtering
                </p>
              </div>
            </div>
          )}

          {/* Step 2: Upload Files */}
          {currentStep === 2 && (
            <div className="space-y-6 animate-fade-in">
              <div>
                <h2 className="text-2xl font-bold mb-2">Upload Files (Optional)</h2>
                <p className="text-gray-600">Add any documents or data files for the agent to analyze</p>
              </div>

              <FileUploader
                onFilesChange={setFiles}
                initialFiles={files}
                maxFiles={10}
                maxSize={10 * 1024 * 1024}
              />

              <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
                <p className="text-sm text-blue-800">
                  <strong>Tip:</strong> You can skip this step if you don't have files to upload.
                  The agent will work with your task description.
                </p>
              </div>
            </div>
          )}

          {/* Step 3: Knowledge Bases */}
          {currentStep === 3 && (
            <div className="space-y-6 animate-fade-in">
              <div>
                <h2 className="text-2xl font-bold mb-2">Select Knowledge Bases (Optional)</h2>
                <p className="text-gray-600">Choose knowledge bases to provide additional context for the agent</p>
              </div>

              {isLoadingKBs ? (
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-black"></div>
                  <p className="ml-3 text-gray-600">Loading knowledge bases...</p>
                </div>
              ) : availableKBs.length === 0 ? (
                <div className="bg-gray-50 border border-gray-200 rounded-md p-8 text-center">
                  <p className="text-gray-600 mb-4">No knowledge bases available</p>
                  <a href="/knowledge-bases/new" className="text-blue-600 hover:underline text-sm">
                    Create your first knowledge base
                  </a>
                </div>
              ) : (
                <>
                  {/* KB Tag Filter */}
                  {kbTags.length > 0 && (
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-sm text-gray-500 mr-1">Filter by tag:</span>
                      <button
                        type="button"
                        onClick={() => setSelectedKbTagFilter(null)}
                        className={`px-3 py-1.5 text-sm rounded-full transition-colors ${
                          selectedKbTagFilter === null
                            ? 'bg-black text-white'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                      >
                        All ({availableKBs.length})
                      </button>
                      {kbTags.map((tag) => (
                        <button
                          key={tag.id}
                          type="button"
                          onClick={() => setSelectedKbTagFilter(tag.id)}
                          className={`px-3 py-1.5 text-sm rounded-full transition-colors flex items-center gap-1.5 ${
                            selectedKbTagFilter === tag.id
                              ? 'bg-black text-white'
                              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                          }`}
                        >
                          {tag.color && (
                            <span
                              className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                              style={{ backgroundColor: selectedKbTagFilter === tag.id ? 'white' : tag.color }}
                            />
                          )}
                          {tag.name} ({tag.kb_count})
                        </button>
                      ))}
                    </div>
                  )}

                  {/* KB List */}
                  <div className="space-y-3">
                    {availableKBs
                      .filter((kb) => {
                        if (selectedKbTagFilter === null) return true;
                        return kb.tags?.some(t => t.id === selectedKbTagFilter);
                      })
                      .map((kb) => {
                        const isDefault = defaultKBIds.includes(kb.id);
                        const isSelected = selectedKBIds.includes(kb.id);

                        return (
                          <div
                            key={kb.id}
                            className={`border rounded-lg p-4 transition-colors cursor-pointer ${
                              isSelected
                                ? 'border-black bg-gray-50'
                                : 'border-gray-200 hover:border-gray-300'
                            }`}
                            onClick={() => toggleKBSelection(kb.id)}
                          >
                            <div className="flex items-start space-x-3">
                              <div className="flex-shrink-0 mt-1">
                                <input
                                  type="checkbox"
                                  checked={isSelected}
                                  onChange={() => toggleKBSelection(kb.id)}
                                  onClick={(e) => e.stopPropagation()}
                                  className="w-5 h-5 rounded border-gray-300"
                                />
                              </div>
                              <div className="flex-1">
                                <div className="flex items-center flex-wrap gap-2">
                                  <h3 className="font-medium text-gray-900">{kb.title}</h3>
                                  {isDefault && (
                                    <span className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs rounded-full">
                                      Default
                                    </span>
                                  )}
                                  {/* KB Tags */}
                                  {kb.tags && kb.tags.length > 0 && kb.tags.map(tag => (
                                    <span
                                      key={tag.id}
                                      className="inline-flex items-center px-2 py-0.5 rounded-full text-xs bg-gray-100 text-gray-600"
                                    >
                                      {tag.color && (
                                        <span
                                          className="w-2 h-2 rounded-full mr-1"
                                          style={{ backgroundColor: tag.color }}
                                        />
                                      )}
                                      {tag.name}
                                    </span>
                                  ))}
                                </div>
                                {kb.description && (
                                  <p className="text-sm text-gray-600 mt-1 line-clamp-2">{kb.description}</p>
                                )}
                                <p className="text-xs text-gray-500 mt-2">
                                  {kb.resource_count} resource{kb.resource_count !== 1 ? 's' : ''}
                                </p>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    {/* Empty state for filtered results */}
                    {selectedKbTagFilter !== null &&
                     availableKBs.filter(kb => kb.tags?.some(t => t.id === selectedKbTagFilter)).length === 0 && (
                      <div className="text-center py-6 text-gray-500 text-sm">
                        No knowledge bases with this tag
                      </div>
                    )}
                  </div>
                </>
              )}

              <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
                <p className="text-sm text-blue-800">
                  <strong>Tip:</strong> Default knowledge bases are automatically pre-selected but can be deselected if needed.
                  You can manage default KBs from the <a href="/knowledge-bases" target="_blank" rel="noopener noreferrer" className="underline hover:text-blue-900">Knowledge Bases</a> page.
                </p>
              </div>
            </div>
          )}

          {/* Step 4: Review */}
          {currentStep === 4 && (
            <div className="space-y-6 animate-fade-in">
              <div>
                <h2 className="text-2xl font-bold mb-2">Review Your Task</h2>
                <p className="text-gray-600">Make sure everything looks correct before submitting</p>
              </div>

              <div className="space-y-4">
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                  <h3 className="text-sm font-medium text-gray-500 mb-1">Task Title</h3>
                  <p className="text-lg font-medium text-gray-900">{title}</p>
                </div>

                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                  <h3 className="text-sm font-medium text-gray-500 mb-2">Description</h3>
                  <p className="text-sm text-gray-900 whitespace-pre-wrap">{description}</p>
                </div>

                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                  <h3 className="text-sm font-medium text-gray-500 mb-2">Files</h3>
                  {files.length > 0 ? (
                    <ul className="text-sm text-gray-900 space-y-1">
                      {files.map((file, index) => (
                        <li key={index} className="flex items-center space-x-2">
                          <svg className="w-4 h-4 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z" clipRule="evenodd" />
                          </svg>
                          <span>{file.name}</span>
                          <span className="text-gray-500">
                            ({(file.size / 1024).toFixed(1)} KB)
                          </span>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="text-sm text-gray-500">No files uploaded</p>
                  )}
                </div>

                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                  <h3 className="text-sm font-medium text-gray-500 mb-2">Knowledge Bases</h3>
                  {selectedKBIds.length > 0 ? (
                    <ul className="text-sm text-gray-900 space-y-1">
                      {selectedKBIds.map((kbId) => {
                        const kb = availableKBs.find(k => k.id === kbId);
                        const isDefault = defaultKBIds.includes(kbId);
                        return kb ? (
                          <li key={kbId} className="flex items-center space-x-2">
                            <svg className="w-4 h-4 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                              <path d="M9 4.804A7.968 7.968 0 005.5 4c-1.255 0-2.443.29-3.5.804v10A7.969 7.969 0 015.5 14c1.669 0 3.218.51 4.5 1.385A7.962 7.962 0 0114.5 14c1.255 0 2.443.29 3.5.804v-10A7.968 7.968 0 0014.5 4c-1.255 0-2.443.29-3.5.804V12a1 1 0 11-2 0V4.804z" />
                            </svg>
                            <span>{kb.title}</span>
                            {isDefault && (
                              <span className="px-1.5 py-0.5 bg-blue-100 text-blue-700 text-xs rounded">
                                Default
                              </span>
                            )}
                          </li>
                        ) : null;
                      })}
                    </ul>
                  ) : (
                    <p className="text-sm text-gray-500">No knowledge bases selected</p>
                  )}
                </div>

                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                  <h3 className="text-sm font-medium text-gray-500 mb-2">Tags</h3>
                  {selectedTagIds.length > 0 ? (
                    <div className="flex flex-wrap gap-2">
                      {selectedTagIds.map((tagId) => {
                        const tag = findTagInTree(tagTree, tagId);
                        return tag ? (
                          <span
                            key={tagId}
                            className="inline-flex items-center px-2.5 py-1 rounded-full text-sm bg-white border border-gray-200"
                          >
                            {tag.color && (
                              <span
                                className="w-2.5 h-2.5 rounded-full mr-1.5"
                                style={{ backgroundColor: tag.color }}
                              />
                            )}
                            <span className="text-gray-700">{getTagPath(tagId)}</span>
                          </span>
                        ) : null;
                      })}
                    </div>
                  ) : (
                    <p className="text-sm text-gray-500">No tags selected</p>
                  )}
                </div>
              </div>

              {error && (
                <div className="p-4 bg-red-50 border border-red-200 rounded-md">
                  <p className="text-sm text-red-600">{error}</p>
                </div>
              )}
            </div>
          )}

          {/* Navigation Buttons */}
          <div className="mt-8 flex items-center justify-between pt-6 border-t border-gray-200">
            <button
              type="button"
              onClick={(e) => currentStep === 1 ? navigate('/tasks') : handleBack(e)}
              className="px-6 py-2 btn-secondary rounded-md text-sm font-medium"
              disabled={isSubmitting}
            >
              {currentStep === 1 ? 'Cancel' : 'Back'}
            </button>

            <div className="flex items-center space-x-3">
              {/* Save as Draft button - available on all steps */}
              <button
                type="button"
                onClick={(e) => handleSubmit(e, true)}
                disabled={isSubmitting || (currentStep === 1 && !canProceedFromStep1)}
                className="px-6 py-2 btn-secondary rounded-md text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? 'Saving...' : 'Save as Draft'}
              </button>

              {currentStep < 4 ? (
                <button
                  type="button"
                  onClick={(e) => handleNext(e)}
                  disabled={currentStep === 1 && !canProceedFromStep1}
                  className="px-6 py-2 btn-primary rounded-md text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              ) : (
                <button
                  type="submit"
                  onClick={(e) => handleSubmit(e, false)}
                  disabled={isSubmitting}
                  className="px-8 py-2 btn-primary rounded-md text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? 'Creating Task...' : 'Submit Task'}
                </button>
              )}
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
